import type { DailyModuleReport } from '@/schemas/reports';
import type { GroundingQuality } from '@/services/Quality';
import { EMPTY_VALUE } from '@/utils/placeholders';

import { DAY_SEGMENT_ORDER, DISTRIBUTION_MIN_BAR_PCT, RANGE_DAYS } from './Quality.constants';
import type { DateRange, DaySegment, GroundingQualityDay, ReportDateWindow } from './Quality.types';

/** `null` means no checks ran in the window, which must not read as 0 %. */
export const formatRate = (rate: number | null): string =>
	rate == null ? EMPTY_VALUE : `${Math.round(rate * 1000) / 10} %`;

/** The busiest day fills the track so the other bars show real relative volume. */
export const maxDayChecks = (byDay: readonly GroundingQualityDay[]): number =>
	Math.max(1, ...byDay.map((d) => d.checks));

export const daySegments = (day: GroundingQualityDay): DaySegment[] =>
	DAY_SEGMENT_ORDER.map((key) => ({ key, value: day[key] }));

export const isEmptyWindow = (data: GroundingQuality): boolean => data.checks === 0;

const toISODay = (date: Date): string => date.toISOString().slice(0, 10);

export const computeReportRange = (range: DateRange, today: Date = new Date()): ReportDateWindow => {
	const fromDate = new Date(today);

	fromDate.setUTCDate(fromDate.getUTCDate() - RANGE_DAYS[range]);
	return { from: toISODay(fromDate), to: toISODay(today) };
};

export const groupByModule = (items: readonly DailyModuleReport[]): Map<string, DailyModuleReport[]> =>
	items.reduce((map, report) => {
		map.set(report.moduleName, [...(map.get(report.moduleName) ?? []), report]);
		return map;
	}, new Map<string, DailyModuleReport[]>());

export const reportKeyOf = (report: DailyModuleReport): string => `${report.moduleName}-${report.day}`;

export const formatReportDay = (iso: string): string => {
	const date = new Date(`${iso}T00:00:00Z`);

	if (Number.isNaN(date.getTime())) return iso;
	// These are UTC buckets: formatting in the viewer's zone would shift every
	// row one day back anywhere west of Greenwich.
	return date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

export const formatAverage = (avg: number | null | undefined): string => (avg == null ? EMPTY_VALUE : avg.toFixed(2));

export const computeResolutionScore = (report: DailyModuleReport | undefined): number | null => {
	if (!report || report.conversationsTotal <= 0) return null;
	return report.closedTotal / report.conversationsTotal;
};

export const distributionBarPct = (count: number, max: number): number =>
	Math.max(DISTRIBUTION_MIN_BAR_PCT, Math.round((count / max) * 100));
